import React, { FC, memo, useEffect, useMemo } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { useWindowSize } from 'react-use';
import { useAppDisaptch } from '../../redux/hooks';
import { useOilsFilters } from '../../hooks/useOilsFilters';
import { useOilSelects } from '../../hooks/useOilSelects';
import { usePositionGeocoding } from '../../hooks/usePositionGeocoding';
import { StringField } from '../../../../types/types';
import { QueryOptions } from '../../types/queryOptions.type';
import { updateLabels } from '../../redux/slices/filters';
import { Filter } from '../../types/OilsFIlter.types';

import './TitleWithFilters.scss';

interface TitleWithFiltersProps {
  title: StringField;
  countriesOptions: {
    id: string;
    name: string;
    countries: string[];
    parentId: string;
  }[];
  queryOptions: QueryOptions;
  labels: { [filter in Filter]: { label: string; placeholder: string } };
}

const TitleWithFilters: FC<TitleWithFiltersProps> = ({
  title,
  countriesOptions,
  queryOptions,
  labels,
}) => {
  const dispatch = useAppDisaptch();
  const { width: screenWidth } = useWindowSize();

  const handleFilterChange = useOilsFilters(countriesOptions, queryOptions);

  let countryCode = null;
  if (typeof window !== 'undefined') {
    countryCode = usePositionGeocoding();
  }

  // ----- automatically set country when location is detected -----
  useEffect(() => {
    if (!countryCode) return;

    const userCountry = countriesOptions.find((c1) => {
      return c1.countries.find((c2) => c2 === countryCode);
    });
    if (!userCountry) return;

    handleFilterChange(userCountry.name, 'country');
  }, [countryCode]);

  const {
    CountryOilSelect,
    ApplicationOilSelect,
    SubApplicationOilSelect,
    SeriesOilSelect,
    HnEOilSelect,
  } = useOilSelects(labels, handleFilterChange);

  // ----- store labels (needed for email) -----

  useEffect(() => {
    let labelsObj: { [filter in Filter]?: string } = {};

    Object.keys(labels).forEach((filter: Filter) => {
      labelsObj[filter] = labels[filter].label;
    });

    dispatch(updateLabels(labelsObj as { [filter in Filter]: string }));
  }, [labels]);

  // ----- screen sizes -----
  const isMobile = useMemo(() => screenWidth <= 850, [screenWidth]);

  return (
    <div className="TitleWithFilters">
      <div className="TitleWithFilters__first_row">
        <div>
          <RichText field={title} tag="h1" />
        </div>
      </div>
      <div className="TitleWithFilters__next_row first">
        <CountryOilSelect />
      </div>
      {isMobile ? (
        <>
          <div className="TitleWithFilters__next_row">
            <ApplicationOilSelect />
          </div>

          <div className="TitleWithFilters__next_row">
            <SubApplicationOilSelect />
          </div>

          <div className="TitleWithFilters__next_row">
            <SeriesOilSelect />
          </div>

          <div className="TitleWithFilters__next_row">
            <HnEOilSelect />
          </div>
        </>
      ) : (
        <>
          <div className="TitleWithFilters__next_row">
            <ApplicationOilSelect />
            <SubApplicationOilSelect />
          </div>
          <div className="TitleWithFilters__next_row">
            <SeriesOilSelect />
            <HnEOilSelect />
          </div>
        </>
      )}
    </div>
  );
};

export default memo(TitleWithFilters);
